import type { AudioSource, AudioStatus } from 'expo-audio'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import {
  RecordingPresets,
  requestRecordingPermissionsAsync,
  setAudioModeAsync,
  useAudioPlayer,
  useAudioPlayerStatus,
  useAudioRecorder,
  useAudioRecorderState,
} from 'expo-audio'

import type { HelperAudioAction } from '../components/PostPaymentRecordStep.state'
import type {
  PostPaymentConfirmedRecordingChange,
  PostPaymentRestoredRecording,
} from '../post-payment-recording'
import {
  INITIAL_RECORD_UI_STATE,
  recordUiReducer,
} from '../components/PostPaymentRecordStep.state'
import { playAudioHelper as playAudioHelperEffect } from '../utils/play-audio-helper'

const PIANO_NOTES_AUDIO_SOURCE = require('../../../../assets/audio/voice-assessment-piano-notes.wav')
const EXAMPLE_AUDIO_SOURCE = require('../../../../assets/audio/voice-assessment-example.wav')
const SIMULTANEOUS_PLAYBACK_RECORDING_PRESET = {
  ...RecordingPresets.HIGH_QUALITY,
  android: {
    ...RecordingPresets.HIGH_QUALITY.android,
    audioSource: 'voice_performance' as const,
  },
}
const PLAYBACK_AUDIO_MODE = {
  playsInSilentMode: true,
  allowsRecording: false,
  interruptionMode: 'mixWithOthers' as const,
}
const RECORDING_AUDIO_MODE = {
  playsInSilentMode: true,
  allowsRecording: true,
  interruptionMode: 'mixWithOthers' as const,
  shouldRouteThroughEarpiece: false,
}
const RECORDED_AUDIO_RETRY_DELAY_MS = 250
const WEB_RECORDING_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  autoGainControl: false,
  channelCount: { ideal: 2 },
  echoCancellation: false,
  noiseSuppression: false,
}

function applyScopedWebRecordingAudioConstraints() {
  if (
    typeof navigator === 'undefined' ||
    navigator.mediaDevices?.getUserMedia == null
  ) {
    return {
      applied: false,
      reason: 'get-user-media-unavailable',
      restore: () => undefined,
    }
  }

  const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
    navigator.mediaDevices,
  )
  navigator.mediaDevices.getUserMedia = ((
    constraints?: MediaStreamConstraints,
  ) => {
    if (constraints?.audio === true) {
      return originalGetUserMedia({
        ...constraints,
        audio: WEB_RECORDING_AUDIO_CONSTRAINTS,
      })
    }

    if (typeof constraints?.audio === 'object') {
      return originalGetUserMedia({
        ...constraints,
        audio: {
          ...WEB_RECORDING_AUDIO_CONSTRAINTS,
          ...constraints.audio,
        },
      })
    }

    return originalGetUserMedia(constraints)
  }) as typeof navigator.mediaDevices.getUserMedia

  return {
    applied: true,
    constraints: WEB_RECORDING_AUDIO_CONSTRAINTS,
    restore: () => {
      navigator.mediaDevices.getUserMedia = originalGetUserMedia
    },
  }
}

async function startWebMicPrewarmStream() {
  if (
    typeof navigator === 'undefined' ||
    navigator.mediaDevices?.getUserMedia == null
  ) {
    return null
  }

  return await navigator.mediaDevices.getUserMedia({
    audio: WEB_RECORDING_AUDIO_CONSTRAINTS,
  })
}

async function startRecorderAndWaitUntilReady(audioRecorder: {
  record: () => void
}) {
  const mediaRecorder = (
    audioRecorder as {
      mediaRecorder?: EventTarget & { state?: string }
    }
  ).mediaRecorder

  if (mediaRecorder == null) {
    audioRecorder.record()
    return
  }

  const started = new Promise<void>((resolve) => {
    mediaRecorder.addEventListener(
      'start',
      () => {
        resolve()
      },
      { once: true },
    )
  })

  audioRecorder.record()
  return await started
}

function shouldRetryRecordedAudioPlayback(status?: {
  playbackState?: string
  playing?: boolean
  isLoaded?: boolean
  reasonForWaitingToPlay?: string
}) {
  if (status == null) {
    return false
  }

  return (
    status.playing !== true &&
    (status.isLoaded !== true ||
      status.playbackState === 'unknown' ||
      status.playbackState === 'loading' ||
      status.reasonForWaitingToPlay === 'evaluatingBufferingRate')
  )
}

function disableAudioPlayerLoop(player: { loop: boolean }) {
  Reflect.set(player, 'loop', false)
}

interface UsePostPaymentRecordControllerOptions {
  audioTrack?: string | null
  restoredRecording?: PostPaymentRestoredRecording | null
  onConfirmedRecordingChange?: PostPaymentConfirmedRecordingChange
}

function resolvePianoAudioSource(audioTrack?: string | null): AudioSource {
  const trimmedAudioTrack = audioTrack?.trim()
  return trimmedAudioTrack != null && trimmedAudioTrack.length > 0
    ? trimmedAudioTrack
    : PIANO_NOTES_AUDIO_SOURCE
}

async function tryFetchRecordingBlob(uri: string): Promise<Blob | null> {
  try {
    const response = await fetch(uri)
    if (!response.ok) {
      return null
    }
    return await response.blob()
  } catch {
    return null
  }
}

function initialRecordUiState(
  restoredRecording:
    | UsePostPaymentRecordControllerOptions['restoredRecording']
    | undefined,
) {
  if (restoredRecording != null) {
    return {
      ...INITIAL_RECORD_UI_STATE,
      step: 'succeeded' as const,
      recordedAudioUri: restoredRecording.recordedAudioUri,
      confirmedRecordingUri: restoredRecording.confirmedRecordingUri,
    }
  }
  return INITIAL_RECORD_UI_STATE
}

export function usePostPaymentRecordController(
  options: UsePostPaymentRecordControllerOptions = {},
) {
  const { audioTrack, restoredRecording, onConfirmedRecordingChange } = options
  const [uiState, uiDispatch] = useReducer(
    recordUiReducer,
    restoredRecording ?? null,
    initialRecordUiState,
  )
  const recordedPlaybackRetryRef = useRef<{
    timeoutId: ReturnType<typeof setTimeout> | null
    resolve: ((shouldContinue: boolean) => void) | null
  }>({
    timeoutId: null,
    resolve: null,
  })
  const micPrewarmRef = useRef<{
    stream: MediaStream | null
    pending: Promise<MediaStream | null> | null
  }>({
    stream: null,
    pending: null,
  })
  const isStoppingRecordingRef = useRef(false)

  const pianoNotesPlayer = useAudioPlayer(resolvePianoAudioSource(audioTrack))
  const examplePlayer = useAudioPlayer(EXAMPLE_AUDIO_SOURCE)
  const recordedAudioPlayer = useAudioPlayer()
  const audioRecorder = useAudioRecorder(SIMULTANEOUS_PLAYBACK_RECORDING_PRESET)
  const recorderState = useAudioRecorderState(audioRecorder)
  const pianoNotesPlayerStatus = useAudioPlayerStatus(pianoNotesPlayer)
  const examplePlayerStatus = useAudioPlayerStatus(examplePlayer)
  const recordedAudioPlayerStatus = useAudioPlayerStatus(recordedAudioPlayer)
  const recordedAudioPlayerStatusRef = useRef<AudioStatus | null>(null)
  const previousPianoDidJustFinishRef = useRef(
    pianoNotesPlayerStatus.didJustFinish === true,
  )

  const isPianoPlaying = pianoNotesPlayerStatus.playing
  const isPianoLooping = pianoNotesPlayerStatus.loop
  const isExamplePlaying = examplePlayerStatus.playing
  const isRecordedAudioPlaying = recordedAudioPlayerStatus.playing
  const elapsedRecordingSeconds = Math.max(
    0,
    Math.floor((recorderState.durationMillis ?? 0) / 1000),
  )

  const stopRecordedAudioPlayback = useCallback(async () => {
    await Promise.resolve(recordedAudioPlayer.pause())
    await Promise.resolve(recordedAudioPlayer.seekTo(0))
  }, [recordedAudioPlayer])

  useEffect(() => {
    recordedAudioPlayerStatusRef.current = recordedAudioPlayerStatus
  }, [recordedAudioPlayerStatus])

  const clearRecordedPlaybackRetry = useCallback(() => {
    const pendingRetry = recordedPlaybackRetryRef.current
    if (pendingRetry.timeoutId != null) {
      clearTimeout(pendingRetry.timeoutId)
      pendingRetry.timeoutId = null
    }
    if (pendingRetry.resolve != null) {
      pendingRetry.resolve(false)
      pendingRetry.resolve = null
    }
  }, [])

  const waitForRecordedPlaybackRetry = useCallback((): Promise<boolean> => {
    clearRecordedPlaybackRetry()

    return new Promise<boolean>((resolve) => {
      recordedPlaybackRetryRef.current.resolve = resolve
      recordedPlaybackRetryRef.current.timeoutId = setTimeout(() => {
        recordedPlaybackRetryRef.current.timeoutId = null
        recordedPlaybackRetryRef.current.resolve = null
        resolve(true)
      }, RECORDED_AUDIO_RETRY_DELAY_MS)
    })
  }, [clearRecordedPlaybackRetry])

  useEffect(() => clearRecordedPlaybackRetry, [clearRecordedPlaybackRetry])

  const stopPianoPlayback = useCallback(async () => {
    disableAudioPlayerLoop(pianoNotesPlayer)
    await Promise.resolve(pianoNotesPlayer.pause())
    await Promise.resolve(pianoNotesPlayer.seekTo(0))
  }, [pianoNotesPlayer])

  const stopMicPrewarm = useCallback(() => {
    const prewarm = micPrewarmRef.current
    prewarm.stream?.getTracks().forEach((track) => {
      track.stop()
    })
    prewarm.stream = null
    prewarm.pending = null
  }, [])

  const ensureMicPrewarm = useCallback(() => {
    const prewarm = micPrewarmRef.current
    if (prewarm.stream != null || prewarm.pending != null) {
      return
    }

    prewarm.pending = startWebMicPrewarmStream()
      .then((stream) => {
        prewarm.pending = null
        prewarm.stream = stream
        return stream
      })
      .catch(() => {
        prewarm.pending = null
        return null
      })
  }, [])

  useEffect(() => stopMicPrewarm, [stopMicPrewarm])

  useEffect(() => {
    if (uiState.step !== 'ready') {
      return
    }

    ensureMicPrewarm()
  }, [ensureMicPrewarm, uiState.step])

  const stopRecordingAndSave = useCallback(async () => {
    if (!recorderState.isRecording || isStoppingRecordingRef.current) {
      return
    }

    isStoppingRecordingRef.current = true
    uiDispatch({ type: 'setStoppingRecording', payload: true })
    uiDispatch({ type: 'setRecordFlowErrorKey', payload: null })

    try {
      clearRecordedPlaybackRetry()
      await audioRecorder.stop()
      const nextRecordedAudioUri = audioRecorder.uri
      if (nextRecordedAudioUri == null) {
        throw new Error('No recording uri available after stop')
      }

      await stopPianoPlayback()

      uiDispatch({
        type: 'setRecordedAudioUri',
        payload: nextRecordedAudioUri,
      })
      uiDispatch({
        type: 'setConfirmedRecordingUri',
        payload: nextRecordedAudioUri,
      })
      const recordingBlob = await tryFetchRecordingBlob(nextRecordedAudioUri)
      onConfirmedRecordingChange?.({
        uri: nextRecordedAudioUri,
        blob: recordingBlob,
      })

      await setAudioModeAsync(PLAYBACK_AUDIO_MODE)
      uiDispatch({ type: 'setStep', payload: 'succeeded' })
    } catch (error) {
      console.error('Stop recording failed', error)
      uiDispatch({ type: 'setRecordFlowErrorKey', payload: 'stop' })
    } finally {
      stopMicPrewarm()
      uiDispatch({ type: 'setStoppingRecording', payload: false })
      isStoppingRecordingRef.current = false
    }
  }, [
    audioRecorder,
    onConfirmedRecordingChange,
    recorderState.isRecording,
    clearRecordedPlaybackRetry,
    stopPianoPlayback,
    stopMicPrewarm,
  ])

  const playAudioHelper = useCallback(
    async (
      action: HelperAudioAction,
      player: {
        play: () => Promise<void> | void
        seekTo: (seconds: number) => Promise<void> | void
      },
      audioMode?: {
        playsInSilentMode: boolean
        allowsRecording: boolean
        interruptionMode?: 'mixWithOthers' | 'doNotMix' | 'duckOthers'
        shouldRouteThroughEarpiece?: boolean
      },
    ) => {
      uiDispatch({ type: 'setHelperActionPending', payload: true })
      uiDispatch({ type: 'setFailedHelperAction', payload: null })

      try {
        await playAudioHelperEffect({
          setAudioMode: setAudioModeAsync,
          player,
          audioMode,
        })
      } catch (error) {
        console.error('Record helper action failed', action, error)
        uiDispatch({ type: 'setFailedHelperAction', payload: action })
      } finally {
        uiDispatch({ type: 'setHelperActionPending', payload: false })
      }
    },
    [],
  )

  const stopAudioHelper = useCallback(
    async (
      action: HelperAudioAction,
      player: {
        pause: () => Promise<void> | void
        seekTo: (seconds: number) => Promise<void> | void
      },
    ) => {
      uiDispatch({ type: 'setHelperActionPending', payload: true })
      uiDispatch({ type: 'setFailedHelperAction', payload: null })

      try {
        await Promise.resolve(player.pause())
        await Promise.resolve(player.seekTo(0))
      } catch (error) {
        console.error('Record helper action failed', action, error)
        uiDispatch({ type: 'setFailedHelperAction', payload: action })
      } finally {
        uiDispatch({ type: 'setHelperActionPending', payload: false })
      }
    },
    [],
  )

  const handlePlayPiano = useCallback(() => {
    if (isPianoPlaying) {
      void stopAudioHelper('play_piano', pianoNotesPlayer)
      return
    }

    if (isExamplePlaying) {
      void stopAudioHelper('play_example', examplePlayer)
    }

    void playAudioHelper('play_piano', pianoNotesPlayer, RECORDING_AUDIO_MODE)
  }, [
    examplePlayer,
    isExamplePlaying,
    isPianoPlaying,
    pianoNotesPlayer,
    playAudioHelper,
    stopAudioHelper,
  ])

  const handlePlayExample = useCallback(() => {
    if (isExamplePlaying) {
      void stopAudioHelper('play_example', examplePlayer)
      return
    }

    if (isPianoPlaying) {
      void stopAudioHelper('play_piano', pianoNotesPlayer)
    }

    void playAudioHelper('play_example', examplePlayer, PLAYBACK_AUDIO_MODE)
  }, [
    examplePlayer,
    isExamplePlaying,
    isPianoPlaying,
    pianoNotesPlayer,
    playAudioHelper,
    stopAudioHelper,
  ])

  const handleStartRecording = useCallback(() => {
    const startRecording = async () => {
      uiDispatch({ type: 'setRecordFlowErrorKey', payload: null })
      uiDispatch({ type: 'setFailedHelperAction', payload: null })
      uiDispatch({ type: 'setStartingRecording', payload: true })
      const scopedAudioConstraints = applyScopedWebRecordingAudioConstraints()

      try {
        clearRecordedPlaybackRetry()
        if (isExamplePlaying) {
          await stopAudioHelper('play_example', examplePlayer)
        }
        if (isRecordedAudioPlaying) {
          await stopRecordedAudioPlayback()
        }

        const permissionResponse = await requestRecordingPermissionsAsync()
        if (!permissionResponse.granted) {
          uiDispatch({ type: 'setRecordFlowErrorKey', payload: 'permission' })
          return
        }
        if (micPrewarmRef.current.pending != null) {
          await micPrewarmRef.current.pending
        }
        uiDispatch({ type: 'setRecordedAudioUri', payload: null })
        uiDispatch({ type: 'setConfirmedRecordingUri', payload: null })
        onConfirmedRecordingChange?.(null)
        await setAudioModeAsync(RECORDING_AUDIO_MODE)
        if (isPianoPlaying || isPianoLooping) {
          await stopPianoPlayback()
        }
        await audioRecorder.prepareToRecordAsync()
        await startRecorderAndWaitUntilReady(audioRecorder)
        await Promise.resolve(pianoNotesPlayer.seekTo(0))
        await Promise.resolve(pianoNotesPlayer.play())
        uiDispatch({ type: 'setStep', payload: 'recording' })
      } catch (error) {
        console.error('Start recording failed', error)
        uiDispatch({ type: 'setRecordFlowErrorKey', payload: 'start' })
      } finally {
        scopedAudioConstraints.restore()
        uiDispatch({ type: 'setStartingRecording', payload: false })
      }
    }

    void startRecording()
  }, [
    audioRecorder,
    examplePlayer,
    isExamplePlaying,
    isPianoLooping,
    isPianoPlaying,
    isRecordedAudioPlaying,
    onConfirmedRecordingChange,
    pianoNotesPlayer,
    clearRecordedPlaybackRetry,
    stopAudioHelper,
    stopPianoPlayback,
    stopRecordedAudioPlayback,
  ])

  const handleStopRecording = useCallback(() => {
    void stopRecordingAndSave()
  }, [stopRecordingAndSave])

  useEffect(() => {
    const didJustFinish = pianoNotesPlayerStatus.didJustFinish === true
    const didTransitionToFinished =
      didJustFinish && !previousPianoDidJustFinishRef.current
    previousPianoDidJustFinishRef.current = didJustFinish

    if (
      uiState.step !== 'recording' ||
      !recorderState.isRecording ||
      !didTransitionToFinished
    ) {
      return
    }

    void stopRecordingAndSave()
  }, [
    pianoNotesPlayerStatus.didJustFinish,
    recorderState.isRecording,
    stopRecordingAndSave,
    uiState.step,
  ])

  const handleListenToRecording = useCallback(() => {
    const playRecordedAudio = async () => {
      if (uiState.recordedAudioUri == null) {
        return
      }

      uiDispatch({ type: 'setRecordFlowErrorKey', payload: null })
      uiDispatch({ type: 'setListeningPending', payload: true })

      try {
        clearRecordedPlaybackRetry()
        if (isRecordedAudioPlaying) {
          await stopRecordedAudioPlayback()
          return
        }

        if (isPianoPlaying || isPianoLooping) {
          await stopPianoPlayback()
        }
        if (isExamplePlaying) {
          await stopAudioHelper('play_example', examplePlayer)
        }

        await setAudioModeAsync(PLAYBACK_AUDIO_MODE)
        recordedAudioPlayer.replace(uiState.recordedAudioUri)
        await Promise.resolve(recordedAudioPlayer.seekTo(0))
        await Promise.resolve(recordedAudioPlayer.play())

        if (
          shouldRetryRecordedAudioPlayback(
            recordedAudioPlayerStatusRef.current ?? undefined,
          )
        ) {
          const shouldContinue = await waitForRecordedPlaybackRetry()
          if (!shouldContinue) {
            return
          }
          await Promise.resolve(recordedAudioPlayer.seekTo(0))
          await Promise.resolve(recordedAudioPlayer.play())
        }
      } catch (error) {
        console.error('Recorded audio playback failed', error)
        uiDispatch({ type: 'setRecordFlowErrorKey', payload: 'playback' })
      } finally {
        uiDispatch({ type: 'setListeningPending', payload: false })
      }
    }

    void playRecordedAudio()
  }, [
    examplePlayer,
    isExamplePlaying,
    isPianoLooping,
    isPianoPlaying,
    isRecordedAudioPlaying,
    recordedAudioPlayer,
    clearRecordedPlaybackRetry,
    stopAudioHelper,
    stopPianoPlayback,
    stopRecordedAudioPlayback,
    uiState.recordedAudioUri,
    waitForRecordedPlaybackRetry,
  ])

  const handleRedoRecording = useCallback(() => {
    if (isRecordedAudioPlaying) {
      void stopRecordedAudioPlayback()
    }
    clearRecordedPlaybackRetry()
    uiDispatch({ type: 'setRecordedAudioUri', payload: null })
    uiDispatch({ type: 'setConfirmedRecordingUri', payload: null })
    onConfirmedRecordingChange?.(null)
    uiDispatch({ type: 'setRecordFlowErrorKey', payload: null })
    uiDispatch({ type: 'setFailedHelperAction', payload: null })
    uiDispatch({ type: 'setStep', payload: 'ready' })
  }, [
    clearRecordedPlaybackRetry,
    isRecordedAudioPlaying,
    onConfirmedRecordingChange,
    stopRecordedAudioPlayback,
  ])

  const handleRetryHelperAction = useCallback(() => {
    if (uiState.failedHelperAction === 'play_piano') {
      void playAudioHelper('play_piano', pianoNotesPlayer, RECORDING_AUDIO_MODE)
      return
    }
    if (uiState.failedHelperAction === 'play_example') {
      void playAudioHelper('play_example', examplePlayer, PLAYBACK_AUDIO_MODE)
    }
  }, [
    examplePlayer,
    pianoNotesPlayer,
    playAudioHelper,
    uiState.failedHelperAction,
  ])

  return {
    uiState,
    isPianoPlaying,
    isExamplePlaying,
    isRecordedAudioPlaying,
    elapsedRecordingSeconds,
    handlePlayPiano,
    handlePlayExample,
    handleStartRecording,
    handleStopRecording,
    handleListenToRecording,
    handleRedoRecording,
    handleRetryHelperAction,
  }
}
